import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { DemoAuthProvider } from './context/DemoAuthContext';
import { SimpleAuthProvider } from './context/SimpleAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProfessionalLanguagePage from './pages/ProfessionalLanguagePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

import FullChatPage from './pages/FullChatPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import AnalyticsPage2025 from './pages/AnalyticsPage2025Fixed';

import OnlineUsersPage from './components/OnlineUsers/OnlineUsersPage';
import ProfessionalThinkingDemo from './components/ProfessionalThinkingDemo';
import DemoLoginPage from './components/DemoAuth/DemoLoginPage';
import UserDemoPage from './components/DemoAuth/UserDemoPage';
import AdminDemoPage from './components/DemoAuth/AdminDemoPage';
import SimpleLoginPage from './components/SimpleAuth/SimpleLoginPage';
import SimpleUserPage from './components/SimpleAuth/SimpleUserPage';
import SimpleAdminPage from './components/SimpleAuth/SimpleAdminPage';
import UserListPage from './pages/UserListPage';
import ContentEditPage from './pages/ContentEditPage';
import SecurityLogPage from './pages/SecurityLogPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RouteProtectionTest from './components/RouteProtectionTest';
import UserStatusHandler from './components/UserStatusHandler';
import DeveloperPageErrorBoundary from './components/DeveloperPageErrorBoundary';

import './css/global/index.css';
import './App.css';
import './utils/activityTracker'; // Initialize activity tracking globally

// CRITICAL: DeveloperPage is REQUIRED - Check #1
// If DeveloperPage is deleted, the application will not work
let DeveloperPage = null;
try {
  DeveloperPage = require('./pages/DeveloperPage').default;
  if (!DeveloperPage) {
    throw new Error('DeveloperPage export is null or undefined');
  }
} catch (error) {
  // CRITICAL ERROR: DeveloperPage is missing - prevent app from running
  console.error('❌ CRITICAL ERROR: DeveloperPage is required but not found!', error);
  console.error('The application cannot run without DeveloperPage. Please restore the file.');
  
  // Create a component that prevents the app from working
  DeveloperPage = () => {
    throw new Error('DeveloperPage is required but has been deleted. The application cannot function without it. Please restore frontend/src/pages/DeveloperPage.jsx');
  };
}

// CRITICAL STARTUP CHECK: Verify DeveloperPage exists - Check #5
// This runs at app initialization to prevent the app from working if DeveloperPage is deleted
if (!DeveloperPage) {
  const errorMsg = 'CRITICAL: DeveloperPage is required but missing. Application cannot start. Please restore frontend/src/pages/DeveloperPage.jsx';
  console.error('❌', errorMsg);
  // Prevent app from rendering
  throw new Error(errorMsg);
}

// Additional validation: Ensure DeveloperPage is a valid React component
if (typeof DeveloperPage !== 'function') {
  const errorMsg = 'CRITICAL: DeveloperPage must be a valid React component. Application cannot start.';
  console.error('❌', errorMsg);
  throw new Error(errorMsg);
}

function App() {
  return (
    <AuthProvider>
      <DemoAuthProvider>
        <SimpleAuthProvider>
          <ThemeProvider>
            <LanguageProvider>
              <Router>
                <div className="App">
                  <UserStatusHandler />
                  <Routes>
                  <Route path="/setup" element={<Navigate to="/login" replace />} />
                  <Route path="/" element={<ProfessionalLanguagePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />

                  <Route path="/fullchat" element={
                    <ProtectedRoute>
                      <FullChatPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/developer" element={
                    <ProtectedRoute>
                      <DeveloperPageErrorBoundary>
                        {/* Check #2: Verify DeveloperPage exists before rendering */}
                        {(() => {
                          if (!DeveloperPage) {
                            throw new Error('DeveloperPage is required but missing. Application cannot function.');
                          }
                          return <DeveloperPage />;
                        })()}
                      </DeveloperPageErrorBoundary>
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AnalyticsPage2025 />
                    </ProtectedRoute>
                  } />

                  <Route path="/userlist" element={
                    <ProtectedRoute requireAdmin={true}>
                      <UserListPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/security" element={
                    <ProtectedRoute requireAdmin={true}>
                      <SecurityLogPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/content-edit" element={
                    <ProtectedRoute requireAdmin={true}>
                      <ContentEditPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/online-users" element={
                    <ProtectedRoute requireAdmin={true}>
                      <OnlineUsersPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/thinking-demo" element={<ProfessionalThinkingDemo />} />
                  <Route path="/route-test" element={<RouteProtectionTest />} />
                  
                  {/* Demo Authentication Routes */}
                  <Route path="/demo-login" element={<DemoLoginPage />} />
                  <Route path="/user-demo" element={<UserDemoPage />} />
                  <Route path="/admin-demo" element={<AdminDemoPage />} />
                  
                  {/* Simple Authentication Routes */}
                  <Route path="/simple-login" element={<SimpleLoginPage />} />
                  <Route path="/simple-user" element={<SimpleUserPage />} />
                  <Route path="/simple-admin" element={<SimpleAdminPage />} />
                  <Route path="/simple-userlist" element={<UserListPage />} />
                  </Routes>
                </div>
              </Router>
            </LanguageProvider>
          </ThemeProvider>
        </SimpleAuthProvider>
      </DemoAuthProvider>
    </AuthProvider>
  );
}

export default App; 
